// One P&P round, step by step — the timeline behind a row of the rounds table.
// Built 2026-09-07 with pp_tracking: every event the picking and packing
// screens recorded, with the gap since the previous one, so "where does the
// time go" can be answered per round rather than guessed.
import React, { useEffect, useState } from 'react';
import { authFetch, API } from '../api';
import { fmtTime, fmtDur } from './shared';

const KIND_LABEL: Record<string, string> = {
  round_started: 'Round started (moved to Printed)', orders_added: 'Orders added', order_excluded: 'Order excluded',
  round_merged: 'Rounds merged', notes_printed: 'Delivery note printed', picker_selected: 'Picker chosen',
  packer_selected: 'Packer chosen', list_shown: 'Picking list shown', picking_started: 'Picking started (boxes grabbed)',
  pick_next: 'Next stop', pick_prev: 'Previous stop', box_swap: 'Box swap', box_swap_continue: 'Continue after box swap',
  pick_done: 'Picking done', button: 'Button', note_scan: 'Delivery note scanned', label_printed: 'Label print',
  item_scan: 'Item scanned', item_missing: 'Item could not be found', note_rescan: 'Delivery note re-scanned', label_scan: 'Label scanned',
  order_packed: 'Order packed', order_processed: 'Order processed', round_ended: 'Round ended', round_cancelled: 'Round cancelled',
};

const gapCls = (s: number | null) => s == null ? 'text-gray-700' : s > 300 ? 'text-red-400' : s > 60 ? 'text-yellow-500' : 'text-gray-500';
const fmtGap = (s: number | null) => s == null ? '' : s < 60 ? `+${Math.round(s)}s` : `+${Math.floor(s / 60)}m ${Math.round(s % 60)}s`;

/** The step's label — a Pack-button tap is NOT a scan and says so (Fernando, 2026-09-28). */
function kindLabel(kind: string, p: any): string {
  if (kind === 'item_scan' && p?.manual) return 'Item packed manually (Pack button)';
  return KIND_LABEL[kind] || kind;
}

function payloadText(kind: string, p: any): string {
  if (!p) return '';
  if (kind === 'button') return [p.name, p.kind, p.auto ? 'automatic' : ''].filter(Boolean).join(' · ');
  if (kind === 'item_scan' && p.manual)
    return `${p.product_code || p.line_id || ''} · no barcode scanned`;
  if (kind === 'item_scan' || kind === 'label_scan' || kind === 'note_scan' || kind === 'note_rescan')
    return (p.ok === false ? '✗ ' : '') + (p.reason || (p.barcode ? `barcode ${p.barcode}` : '')) + (p.remaining != null ? ` · ${p.remaining} left` : '')
      + (p.cart ? ` · cart ${p.cart}` : '');
  if (kind === 'order_packed') {
    // what was booked per line — pieces, and how many of them by the Pack button
    const lines: any[] = Array.isArray(p.packed) ? p.packed : [];
    if (!lines.length) return '';
    const pcs = lines.reduce((n, l) => n + (Number(l.qty) || 0), 0);
    const manual = lines.reduce((n, l) => n + (Number(l.manual) || 0), 0);
    return `${pcs} piece${pcs === 1 ? '' : 's'} on ${lines.length} line${lines.length === 1 ? '' : 's'}`
      + (manual ? ` · ${manual} packed manually` : ' · all scanned')
      + ': ' + lines.map(l => `${l.product_code || l.line_id} ×${l.qty}${l.manual ? ` (${l.manual} manual)` : ''}`).join(', ');
  }
  if (kind === 'pick_next' || kind === 'pick_prev') {
    const f = p.from || {};
    return [f.aisle, ...(f.left || []), ...(f.right || [])].filter(Boolean).join(' ') + (f.qty != null ? ` · ${f.qty} pcs` : '');
  }
  if (kind === 'label_printed') return p.status || '';
  if (kind === 'item_missing') return `${p.missing} of ${p.ordered} missing · ${p.product_code || p.line_id} → ${p.to_tab}`;
  if (kind === 'order_excluded') return `→ ${p.to_tab}`;
  if (kind === 'list_shown') return `${p.orders} orders · ${p.stops} stops`;
  if (kind === 'box_swap') return `after trip ${p.after_load ?? '?'} · ${p.reason === 'button' ? 'Box swap button' : 'last stop of the trip'}`;
  if (kind === 'box_swap_continue') return `trip ${p.load ?? '?'} · boxes ${(p.boxes || []).join(', ')}`;
  if (kind === 'picking_started') return `boxes ${(p.boxes || []).join(', ')}`;
  return Object.entries(p).filter(([k]) => k !== 'from').map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : String(v)}`).join(' · ');
}

export default function RoundDetail({ roundId, onClose }: { roundId: number; onClose: () => void }) {
  const [data, setData] = useState<any | null>(null);
  const [error, setError] = useState('');
  useEffect(() => {
    authFetch(`${API}/pp/rounds/${roundId}`).then(async r => {
      if (!r.ok) { setError((await r.json().catch(() => ({} as any))).detail || 'Could not load the round'); return; }
      setData(await r.json());
    }).catch(() => setError('Could not load the round'));
  }, [roundId]);

  const r = data?.round;
  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-start justify-center overflow-y-auto py-8" onClick={onClose}>
      <div className="bg-[#1a1d27] border border-gray-800 rounded-2xl w-full max-w-5xl mx-4" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-gray-800 flex items-center justify-between">
          <div>
            <h2 className="text-white font-semibold">P&P round #{roundId}{r ? ` — ${r.date}` : ''}</h2>
            {r && (
              <p className="text-xs text-gray-500 mt-0.5">
                Started {fmtTime(r.round_start)} by {r.starter || '—'} · picker {r.picker || '—'}{r.picker_device ? ` (${r.picker_device})` : ''} · packer {r.packers || '—'}
                {' · '}{r.orders} orders{r.diverted ? ` (${r.diverted} diverted: ${r.diverted_to || ''})` : ''}{r.excluded ? ` · ${r.excluded} excluded` : ''} · {r.archived_orders} processed
                {' · '}picking {fmtDur(r.picking_minutes)}{r.picking_pct != null ? ` (${r.picking_pct}%)` : ''} · packing {fmtDur(r.packing_minutes)}{r.packing_pct != null ? ` (${r.packing_pct}%)` : ''} · total {fmtDur(r.duration_minutes)}{r.auto_closed ? ' · auto-closed' : ''}
                {r.orders_per_hour != null ? ` · ${r.orders_per_hour}/hr` : ''}{r.metres != null ? ` · walked ${Math.round(r.metres)} m (${r.metres_per_order} m/order)` : ''}{r.swaps ? ` · ${r.swaps} box swap${r.swaps === 1 ? '' : 's'}${r.swap_minutes != null ? ` (${fmtDur(r.swap_minutes)}${r.metres_swaps ? `, +${Math.round(r.metres_swaps)} m` : ''})` : r.metres_swaps ? ` (+${Math.round(r.metres_swaps)} m)` : ''}` : ''} · flow {r.flow_key} · {r.status}
              </p>
            )}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white text-sm px-3 py-1.5 bg-gray-800 rounded-lg">✕ Close</button>
        </div>
        {error && <div className="px-6 py-4 text-red-400 text-sm">{error}</div>}
        {!data && !error && <div className="px-6 py-10 text-gray-500 text-sm text-center">Loading…</div>}
        {data && (
          <div className="grid md:grid-cols-[280px_1fr]">
            <div className="border-r border-gray-800 p-4">
              {r?.split && r.split.walking_sec != null && (
                <div className="mb-4">
                  <div className="text-xs text-gray-500 uppercase tracking-wider mb-2">Picking time split</div>
                  <div className="space-y-1 text-sm">
                    {([
                      ['Walking', r.split.walking_sec, `${Math.round(r.split.metres)} m at ${r.split.speed} m/s`],
                      ['At the shelves', r.split.shelf_sec, `${r.split.stops} stops, ${r.split.pieces} pcs`],
                      ['Box changes', r.split.box_change_sec, `${r.split.swaps}`],
                      ['Checks (Back)', r.split.check_sec, `${r.split.checks}`],
                      ['Other', r.split.other_sec, 'pauses, searching'],
                    ] as [string, number | null, string][]).map(([k, v, sub]) => v != null && (v > 0 || k === 'Other') ? (
                      <div key={k} className="flex justify-between gap-2">
                        <span className="text-gray-300">{k} <span className="text-gray-600 text-xs">{sub}</span></span>
                        <span className="text-gray-200 whitespace-nowrap">{fmtDur(v / 60)}{r.split.picking_sec ? <span className="text-gray-600 text-xs"> {Math.round(100 * v / r.split.picking_sec)}%</span> : null}</span>
                      </div>
                    ) : null)}
                    {r.split.model_over > 0 && <div className="text-[11px] text-emerald-300">Faster than this person's average pace by {fmtDur(r.split.model_over / 60)} — the parts are scaled to the real picking time.</div>}
                    <div className="text-[11px] text-gray-600">by {r.pace_source === 'own' ? "this person's own pace" : 'the warehouse average pace'}{r.tapped_through ? ' · tapped through: not in the fit' : ''}</div>
                  </div>
                </div>
              )}
              <div className="text-xs text-gray-500 uppercase tracking-wider mb-2">Orders</div>
              <div className="space-y-1.5 text-sm">
                {data.orders.map((o: any) => (
                  <div key={o.order_id} className={`flex justify-between ${o.excluded_at ? 'opacity-50' : ''}`}>
                    <span className="text-gray-200">{o.order_number || o.order_id}</span>
                    <span className="text-xs text-gray-500">
                      {o.excluded_at ? `${o.excluded_kind === 'diverted' ? 'diverted' : 'excluded'} → ${o.excluded_reason}` : o.processed_at ? `processed ${fmtTime(o.processed_at)}` : o.packed_at ? `packed ${fmtTime(o.packed_at)}` : o.note_printed_at ? 'note printed' : 'open'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
            <div className="p-4 overflow-x-auto">
              <div className="text-xs text-gray-500 uppercase tracking-wider mb-2">Timeline ({data.events.length} steps)</div>
              <table className="w-full text-xs">
                <tbody>
                  {data.events.map((e: any) => (
                    <tr key={e.id} className="border-b border-gray-800/40">
                      <td className="py-1 pr-3 text-gray-400 whitespace-nowrap">{fmtTime(e.at)}</td>
                      <td className={`py-1 pr-3 whitespace-nowrap text-right ${gapCls(e.gap_seconds)}`}>{fmtGap(e.gap_seconds)}</td>
                      <td className={`py-1 pr-3 whitespace-nowrap ${e.kind === 'item_scan' && e.payload?.manual ? 'text-amber-300' : 'text-gray-200'}`}>{kindLabel(e.kind, e.payload)}</td>
                      <td className="py-1 pr-3 text-gray-400 whitespace-nowrap">{e.order_number || ''}</td>
                      <td className="py-1 pr-3 text-gray-500 max-w-md">{payloadText(e.kind, e.payload)}
                        {e.swap_seconds != null && <span className="ml-1 text-sky-300" title="Swap screen → Continue, on the tablet's clock: the walk to the station and the box change">· swap took {e.swap_seconds < 60 ? `${Math.round(e.swap_seconds)} s` : `${Math.floor(e.swap_seconds / 60)} min ${Math.round(e.swap_seconds % 60)} s`}</span>}
                        {e.est?.walk_sec != null && e.est.pick_sec != null && <span className="ml-1 text-gray-600" title="Estimate: the walk at this person's pace, the rest of the step is the picking">· ≈ {Math.round(e.est.walk_sec)} s walking ({e.est.metres} m) · ≈ {Math.round(e.est.pick_sec)} s picking</span>}
                        {e.est?.box_sec != null && <span className="ml-1 text-gray-600">· ≈ {Math.round(e.est.walk_sec)} s walking ({e.est.metres} m) · ≈ {Math.round(e.est.box_sec)} s box change</span>}
                        {e.est?.check_sec != null && <span className="ml-1 text-gray-600">· re-check, not a walk</span>}</td>
                      <td className="py-1 text-gray-600 whitespace-nowrap">{e.actor || ''}{e.device ? ` · ${e.device}` : ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
